import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as wasm from 'sched';
import Avatar from '../common/Avatar';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { AllocationModal, type AllocationBarInfo } from './AllocationModal';
import { AllocationCreateModal } from './AllocationCreateModal';
import {
  CalendarTimeline,
  type CalendarBar,
  type CalendarRow,
  type VariantStyle,
} from './CalendarTimeline';

function fmtEffort(tenths: number): string {
  if (tenths === 0) return '0';
  const days = tenths / 10;
  return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
}

type Variant = 'committed' | 'draft' | 'suggestion';

const VARIANT_STYLES: Record<Variant, VariantStyle> = {
  committed: {
    idle: 'bg-indigo-500/70 text-white',
    hover: 'bg-indigo-500/90 text-white',
  },
  draft: {
    idle: 'bg-amber-500/50 text-amber-100 border border-dashed border-amber-400/50',
    hover: 'bg-amber-500/70 text-amber-100 border border-dashed border-amber-400/50',
  },
  suggestion: {
    idle: 'bg-teal-500/15 text-teal-300/80 border border-dashed border-teal-500/30',
    hover: 'bg-teal-500/30 text-teal-300/80 border border-dashed border-teal-500/30',
  },
};

interface PlanningGridProps {
  result: wasm.PlanningGridResult;
  /** Team that owns the planning context — passed to the create modal so it
   *  can walk the team's priority list when ranking candidate tasks. */
  teamId: string;
  /** In-flight planning grid drafts. The create modal forwards these to the
   *  candidate-tasks helper so planned-effort signals include unsaved work. */
  drafts: import('../../hooks/usePlanningGrid').DraftAllocation[];
  onAcceptSuggestion: (personId: string, dayIdx: number, suggestion: wasm.PlannedAllocationDto) => void;
  onAddAllocation: (personId: string, dayIdx: number, entityId: string, effort: number) => void;
  onRemoveDraft: (personId: string, dayIdx: number, draftIdx: number) => void;
}

interface ExtractedBars {
  committed: BarData[];
  draft: BarData[];
  suggestions: BarData[];
}

interface BarData {
  label: string;
  totalEffort: number;
  startIdx: number;
  endIdx: number;
  alloc: any;
  personId: string;
}

/** Extract contiguous bars per variant from a person's per-day cells. */
function extractBars(person: wasm.PersonPlanDto, days: any[], personId: string): ExtractedBars {
  const committed: BarData[] = [];
  const draft: BarData[] = [];
  const suggestions: BarData[] = [];
  // Keyed lookup for run-length merging of adjacent days into bars.
  const seen: Record<string, BarData> = {};

  for (let i = 0; i < days.length; i++) {
    const cell = person.cells[i.toString()];
    if (!cell) continue;
    for (const [field, target] of [
      ['committed', committed],
      ['draft', draft],
      ['suggestions', suggestions],
    ] as const) {
      const allocs: any[] = (cell as any)[field] ?? [];
      for (const alloc of allocs) {
        const key = `${field}:${alloc.allocation_id || `${alloc.label}-${(alloc.entity as any)?.id ?? ''}`}`;
        const existing = seen[key];
        if (existing) {
          existing.endIdx = i;
        } else {
          const bar: BarData = {
            label: alloc.label,
            totalEffort: (alloc as any).effort ?? 0,
            startIdx: i,
            endIdx: i,
            alloc,
            personId,
          };
          seen[key] = bar;
          target.push(bar);
        }
      }
    }
  }
  return { committed, draft, suggestions };
}

export const PlanningGrid: React.FC<PlanningGridProps> = ({ result, teamId, drafts }) => {
  const days: any[] = (result as any).days ?? [];
  const periods: any[] = (result as any).periods ?? [];
  const people = result.people;
  const { workspaceId, database, applyCommand } = useDatabase();
  const { buildUrl } = useWorkspace();

  const [showSuggestions, setShowSuggestions] = useState(false);

  const [modalState, setModalState] = useState<{
    bar: AllocationBarInfo;
    variant: Variant;
    personCells: Record<string, any>;
  } | null>(null);

  const [createState, setCreateState] = useState<{
    personId: string;
    personName: string;
    startDate: string;
    endDate: string;
    personCells: Record<string, any>;
  } | null>(null);

  const sortedPeople = useMemo(
    () => Object.entries(people).sort(([, a], [, b]) => a.name.localeCompare(b.name)),
    [people],
  );

  // Per-person bars indexed by person id, used both for rendering rows and
  // for resolving allocation_ids when an edge-resize commits.
  const personBars = useMemo(() => {
    const out: Record<string, ExtractedBars> = {};
    for (const [personId, person] of sortedPeople) {
      out[personId] = extractBars(person, days, personId);
    }
    return out;
  }, [sortedPeople, days]);

  // Build one CalendarRow per person.
  const rows: CalendarRow[] = useMemo(() => {
    return sortedPeople.map(([personId, person]) => {
      const { committed, draft, suggestions } = personBars[personId];

      const openBar = (bar: BarData, variant: Variant) => {
        setModalState({
          bar: bar as AllocationBarInfo,
          variant,
          personCells: person.cells as Record<string, any>,
        });
      };

      const toCalendarBar = (bar: BarData, variant: Variant): CalendarBar => ({
        // Resize updates a bar's start/end; key it on alloc id (or label) so
        // identity is stable across the in-flight transformation.
        key: `${variant}:${bar.alloc.allocation_id ?? bar.label}`,
        label: bar.label,
        startIdx: bar.startIdx,
        endIdx: bar.endIdx,
        variant,
        meta: fmtEffort(bar.totalEffort),
        tooltipSuffix:
          variant === 'committed'
            ? '(click to edit, drag edge to resize)'
            : variant === 'suggestion'
              ? '(click to schedule)'
              : undefined,
        onClick: () => openBar(bar, variant),
        // Edges are draggable for committed and draft allocations. Suggestions
        // aren't real bars so we don't resize them.
        resizable: variant !== 'suggestion',
      });

      const bars: CalendarBar[] = [
        ...committed.map((b) => toCalendarBar(b, 'committed')),
        ...draft.map((b) => toCalendarBar(b, 'draft')),
      ];
      if (showSuggestions) {
        for (const s of suggestions) bars.push(toCalendarBar(s, 'suggestion'));
      }

      return {
        key: personId,
        labelContent: (
          <>
            <Avatar imageUrl={person.avatar_url} name={person.name} showTooltip={false} size="sm" />
            <Link
              to={buildUrl({ kind: 'person', personId })}
              className="text-xs font-medium truncate hover:text-cm-accent-blue transition-colors"
            >
              {person.name}
            </Link>
          </>
        ),
        bars,
      };
    });
  }, [sortedPeople, personBars, showSuggestions, buildUrl]);

  // ─── Drag callbacks ─────────────────────────────────────────────
  const handleRangeCreate = (personId: string, startIdx: number, endIdx: number) => {
    const person = people[personId];
    if (!person) return;
    const startDate = days[startIdx]?.date;
    const endDate = days[endIdx]?.date;
    if (!startDate || !endDate) return;
    setCreateState({
      personId,
      personName: person.name,
      startDate,
      endDate,
      personCells: person.cells as Record<string, any>,
    });
  };

  // Edge-resize on a committed/draft bar — replace the existing allocation
  // with a new date range while preserving the entity, person, and effort.
  const handleRangeResize = (
    personId: string,
    barKey: string,
    startIdx: number,
    endIdx: number,
  ) => {
    const startDate = days[startIdx]?.date;
    const endDate = days[endIdx]?.date;
    if (!startDate || !endDate) return;

    // barKey format: `${variant}:${alloc.allocation_id ?? label}`
    const bars = personBars[personId];
    if (!bars) return;
    const [variant] = barKey.split(':') as [Variant];
    const list = variant === 'committed' ? bars.committed : variant === 'draft' ? bars.draft : null;
    if (!list) return;
    const bar = list.find((b) => `${variant}:${b.alloc.allocation_id ?? b.label}` === barKey);
    if (!bar) return;

    const allocationId = bar.alloc?.allocation_id;
    const entityId = (bar.alloc?.entity as any)?.id;
    if (!allocationId || !entityId) return;

    const epoch =
      startDate === endDate
        ? ({ type: 'Day' as const, day: startDate })
        : ({ type: 'Days' as const, start_inclusive: startDate, end_inclusive: endDate });

    applyCommand({
      type: 'ReplaceResourceAllocation',
      allocation_id: allocationId,
      entity: entityId,
      person_id: personId,
      time: bar.totalEffort,
      when: epoch,
    } as wasm.DatabaseCommand);
  };

  return (
    <>
      <div className="flex items-center justify-end mb-2">
        <label className="flex items-center gap-2 text-xs text-cm-text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showSuggestions}
            onChange={(e) => setShowSuggestions(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-cm-border-mid bg-cm-bg-deep text-cm-accent-blue focus:ring-cm-accent-blue/40"
          />
          Show suggestions
        </label>
      </div>

      <CalendarTimeline
        days={days}
        periods={periods}
        rows={rows}
        variantStyles={VARIANT_STYLES}
        emptyMessage="No schedule data."
        onRangeCreate={handleRangeCreate}
        onRangeResize={handleRangeResize}
      />
      <p className="mt-2 text-[11px] text-cm-text-muted">
        Click a column to schedule for that period; drag to span multiple. Drag a bar's edge to resize.
      </p>

      {modalState && (
        <AllocationModal
          bar={modalState.bar}
          variant={modalState.variant}
          days={days}
          workspaceId={workspaceId}
          database={database}
          personCells={modalState.personCells}
          applyCommand={applyCommand}
          onClose={() => setModalState(null)}
        />
      )}

      {createState && (
        <AllocationCreateModal
          personId={createState.personId}
          personName={createState.personName}
          teamId={teamId}
          startDate={createState.startDate}
          endDate={createState.endDate}
          personCells={createState.personCells}
          days={days}
          database={database}
          drafts={drafts}
          applyCommand={applyCommand}
          onClose={() => setCreateState(null)}
        />
      )}
    </>
  );
};
